import { DatePicker, Select } from "antd";
import dayjs from "dayjs";
import { useUserStore } from "../../../../store/userStore";
import { useEffect } from "react";

export default function DashboardFilters({
  isAgent,
  isTeamLeader,
  isSales,
  filters,
  setFilters,
  offices,
  teamLeaders,
  agents,
  countries = [],
  sources = [],
  secondaryOffices = [],
  isSecondaryOfficesLoading = false,
  showCountrySource = false,
  extraFilters = [],
  rightActions = null,
  filterTitle = "Filters",
}) {
  const today = dayjs().endOf("day");
  const userDetails = useUserStore((state) => state.userDetails);

  useEffect(() => {
    if (isSales) {
      setFilters((prev) => ({
        ...prev,
        teamLeadId: userDetails?.isTeamLeader ? userDetails?.userId : null,
      }));
    }
  }, [isSales, userDetails?.isTeamLeader, userDetails?.userId, setFilters]);

  const hasAnyFilter =
    filters.from ||
    filters.to ||
    filters.officeId ||
    filters.teamLeadId ||
    filters.agentId ||
    filters.countryId ||
    filters.source ||
    filters.secondaryOfficeId;

  const handleReset = () => {
    setFilters((prev) => ({
      from: undefined,
      to: undefined,
      officeId: undefined,
      teamLeadId:
        isSales && userDetails?.isTeamLeader ? userDetails?.userId : null,
      agentId: undefined,
      countryId: undefined,
      source: undefined,
      secondaryOfficeId: undefined,
    }));
  };

  const renderFilter = (
    label,
    value,
    options,
    onChange,
    width,
    disabled = false,
    placeholder = undefined,
    showSearch = false,
    loading = false,
  ) => (
    <div className="flex flex-col gap-1.5 min-w-0 ant-multi-select-selector ant-select-selector-white">
      <label className="text-sm font-medium text-gray-600">{label}</label>
      <Select
        value={value}
        options={options}
        onChange={onChange}
        disabled={disabled}
        loading={loading}
        allowClear
        className="w-full"
        placeholder={placeholder}
        showSearch={showSearch}
        {...(showSearch
          ? {
              optionFilterProp: "label",
              filterOption: (input, option) =>
                String(option?.label ?? "")
                  .toLowerCase()
                  .includes(String(input).toLowerCase()),
            }
          : {})}
      />
    </div>
  );

  const renderDateFilter = (label, value, onChange, disabledDate) => (
    <div className="flex flex-col gap-1.5 min-w-0">
      <label className="text-sm font-medium text-gray-600">{label}</label>
      <DatePicker
        value={value ? dayjs(value, "YYYY-MM-DD") : null}
        format="YYYY-MM-DD"
        allowClear
        className="w-full"
        disabledDate={disabledDate}
        onChange={(date) =>
          onChange(date ? date.format("YYYY-MM-DD") : undefined)
        }
      />
    </div>
  );

  const filterItems = [];

  filterItems.push(
    renderDateFilter(
      "From",
      filters.from,
      (value) => setFilters((prev) => ({ ...prev, from: value })),
      (current) => {
        if (!current) return false;
        if (current.isAfter(today, "day")) return true;
        if (!filters.to) return false;
        return current.isAfter(dayjs(filters.to, "YYYY-MM-DD"), "day");
      },
    ),
  );

  filterItems.push(
    renderDateFilter(
      "To",
      filters.to,
      (value) => setFilters((prev) => ({ ...prev, to: value })),
      (current) => {
        if (!current) return false;
        if (current.isAfter(today, "day")) return true;
        if (!filters.from) return false;
        return current.isBefore(dayjs(filters.from, "YYYY-MM-DD"), "day");
      },
    ),
  );

  if (!isTeamLeader && !isAgent) {
    filterItems.push(
      renderFilter(
        "Office",
        filters.officeId,
        offices,
        (value) =>
          setFilters((prev) => ({
            ...prev,
            officeId: value,
            teamLeadId: undefined,
            agentId: undefined,
          })),
        undefined,
        undefined,
        "Select Office",
      ),
    );
  }

  if (!isTeamLeader && !isAgent) {
    filterItems.push(
      renderFilter(
        "Team Leader",
        filters.teamLeadId,
        teamLeaders,
        (value) =>
          setFilters((prev) => ({
            ...prev,
            teamLeadId: value,
            agentId: undefined,
          })),
        undefined,
        !filters.officeId,
        "Select Leader",
      ),
    );
  }

  if (!isAgent) {
    filterItems.push(
      renderFilter(
        "Agent",
        filters.agentId,
        isTeamLeader
          ? [...agents, { label: "Me", value: userDetails?.userId }]
          : agents,
        (value) => setFilters((prev) => ({ ...prev, agentId: value })),
        undefined,
        !filters.teamLeadId,
        "Select Agent",
      ),
    );
  }

  if (showCountrySource) {
    filterItems.push(
      renderFilter(
        "Country",
        filters.countryId,
        countries,
        (value) => setFilters((prev) => ({ ...prev, countryId: value })),
        undefined,
        undefined,
        "Select a country",
        true,
      ),
    );
  }

  if (isSales) {
    filterItems.push(
      renderFilter(
        "Secondary Offices",
        filters.secondaryOfficeId,
        secondaryOffices,
        (value) =>
          setFilters((prev) => ({ ...prev, secondaryOfficeId: value })),
        undefined,
        isSecondaryOfficesLoading,
        "Select secondary office",
        true,
        isSecondaryOfficesLoading,
      ),
    );
  }

  if (showCountrySource && !isTeamLeader && !isAgent) {
    filterItems.push(
      renderFilter(
        "Source",
        filters.source,
        sources,
        (value) => setFilters((prev) => ({ ...prev, source: value })),
        undefined,
        undefined,
        "Select source",
      ),
    );
  }

  (extraFilters || []).forEach((item) => {
    filterItems.push(
      renderFilter(
        item?.label,
        filters?.[item?.key],
        item?.options || [],
        (value) =>
          setFilters((prev) => ({
            ...prev,
            [item?.key]: value,
          })),
        item?.width,
        item?.disabled,
        item?.placeholder,
        item?.showSearch,
      ),
    );
  });

  const gridCols =
    filterItems.length <= 4
      ? "grid-cols-2 md:grid-cols-4"
      : filterItems.length <= 6
        ? "grid-cols-2 md:grid-cols-3 xl:grid-cols-6"
        : "grid-cols-2 md:grid-cols-4 xl:grid-cols-7";

  return (
    <div className="border-2 border-light rounded-[10px] p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-title font-semibold">{filterTitle}</h2>
        <div className="flex items-center gap-3">
          {rightActions}
          {hasAnyFilter && (
            <button
              onClick={handleReset}
              className="text-sm text-primary font-medium hover:underline"
            >
              Reset All
            </button>
          )}
        </div>
      </div>
      <div className={`grid ${gridCols} gap-4`}>
        {filterItems.map((item, idx) => (
          <div key={idx}>{item}</div>
        ))}
      </div>
    </div>
  );
}
